import React from 'react';

/** How far one arrow-key press moves a column edge. */
const KEYBOARD_STEP = 16;

export interface ColumnResizeHandleProps {
  /** Current width, when known — omitted while the table is still on its auto layout. */
  width?: number;
  minWidth: number;
  maxWidth: number;
  /** True while this column is being dragged — keeps the guide line visible. */
  active?: boolean;
  onResizeStart: (e: React.PointerEvent<HTMLElement>) => void;
  /** Double-click: back to the column's default width. */
  onReset: () => void;
  /** Arrow keys: widen or narrow by `delta` pixels. `el` is the handle, for measuring its table. */
  onNudge: (delta: number, el: HTMLElement) => void;
  /** Accessible name, e.g. "Resize Title column". */
  label?: string;
  /**
   * Draw a short divider at rest, so the edge reads as draggable before it is
   * hovered. For tables without cell borders; a gridded table already shows
   * its column edges and leaves this off.
   */
  showDivider?: boolean;
}

/**
 * Drag strip on the right edge of a table header cell. The header must be
 * `position: relative`. Drive it with `useColumnResize().handleProps(key)`.
 *
 * The strip straddles the column edge for an easier grab; its lines sit just
 * inside the edge, so a header that clips overflow still shows them whole.
 */
export const ColumnResizeHandle: React.FC<ColumnResizeHandleProps> = ({
  width,
  minWidth,
  maxWidth,
  active = false,
  onResizeStart,
  onReset,
  onNudge,
  label,
  showDivider = false,
}) => (
  <div
    role="separator"
    aria-orientation="vertical"
    aria-label={label}
    aria-valuenow={width}
    aria-valuemin={minWidth}
    aria-valuemax={maxWidth}
    tabIndex={0}
    onPointerDown={onResizeStart}
    onClick={(e) => e.stopPropagation()}
    onDoubleClick={(e) => {
      e.stopPropagation();
      onReset();
    }}
    onKeyDown={(e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      e.stopPropagation();
      onNudge(e.key === 'ArrowLeft' ? -KEYBOARD_STEP : KEYBOARD_STEP, e.currentTarget);
    }}
    className="group/resize absolute top-0 -right-bg-v2-xs z-20 h-full w-bg-v2-md cursor-col-resize touch-none outline-none"
  >
    {showDivider && (
      <div className="absolute top-1/2 left-1/2 h-1/2 w-px -translate-x-full -translate-y-1/2 bg-border-primary" />
    )}
    <div
      className={`absolute inset-y-0 left-1/2 w-px -translate-x-full bg-border-brand transition-opacity group-hover/resize:opacity-100 group-focus-visible/resize:opacity-100 ${active ? 'opacity-100' : 'opacity-0'}`}
    />
  </div>
);
